import React, { useState, useCallback, useEffect, useMemo, memo } from "react";
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Grid,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  FormHelperText,
  FormControlLabel,
  Checkbox,
  LinearProgress,
  Alert,
  Tooltip,
  Pagination,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Autocomplete,
} from "@mui/material";
import {
  CloudUpload as UploadIcon,
  Refresh as RefreshIcon,
  Cancel as CancelIcon,
  Replay as RetryIcon,
  Visibility as ViewIcon,
  AutoAwesome as AutoIcon,
  Warning as WarningIcon,
  Delete as DeleteIcon,
} from "@mui/icons-material";
import { useDropzone } from "react-dropzone";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useSnackbar } from "notistack";
import { 
  uploadsAPI,
  categoriesAPI,
  databaseAPI,
  columnMappingAPI,
  smartMergeAPI,
} from "../services/api.js";
import SyncService from "../services/syncService.js";
import { format } from "date-fns";
import ColumnMappingDialog from "../components/ColumnMappingDialog.jsx";
import ColumnMergeDialog from "../components/ColumnMergeDialog.jsx";
import ErrorBoundary from "../components/ErrorBoundary.jsx";

// Memoized StatusChip component to prevent unnecessary re-renders
const StatusChip = memo(({ status }) => {
  const statusProps = useMemo(() => {
    switch (status) {
      case "pending":
        return { color: "warning", label: "Pending" };
      case "processing":
        return { color: "info", label: "Processing" };
      case "completed":
        return { color: "success", label: "Completed" };
      case "failed":
        return { color: "error", label: "Failed" };
      case "cancelled":
        return { color: "default", label: "Cancelled" };
      default:
        return { color: "default", label: status };
    }
  }, [status]);

  return <Chip size="small" {...statusProps} />;
});

StatusChip.displayName = "StatusChip";

const UploadDialog = ({ open, onClose, categories }) => {
  // Reset form when dialog opens/closes
  React.useEffect(() => {
    if (open) {
      // Reset form when dialog opens
      setFormData({
        category_id: "",
        database_id: "",
        target_table: "",
        create_new_table: false,
      });
      setSelectedFile(null);
      setUploadProgress(0);
      setUploading(false);
      setSuggestedTableName("");
      setTableNameAutoGenerated(false);
      setDuplicateWarning(null);
      setCheckingDuplicate(false);
      setShowColumnMapping(false);
      setColumnMappings(null);
      setNeedsColumnMapping(false);
    }
  }, [open]);
  const [selectedFile, setSelectedFile] = useState(null);
  const [formData, setFormData] = useState({
    category_id: "",
    database_id: "", // Start with empty value, will be set when databases load
    target_table: "",
    create_new_table: false,
  });
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploading, setUploading] = useState(false);

  // New state for enhanced features
  const [suggestedTableName, setSuggestedTableName] = useState("");
  const [tableNameAutoGenerated, setTableNameAutoGenerated] = useState(false);
  const [duplicateWarning, setDuplicateWarning] = useState(null);
  const [checkingDuplicate, setCheckingDuplicate] = useState(false);

  // Column mapping state
  const [showColumnMapping, setShowColumnMapping] = useState(false);
  const [columnMappings, setColumnMappings] = useState(null);
  const [needsColumnMapping, setNeedsColumnMapping] = useState(false);

  // Smart merge state
  const [showSmartMerge, setShowSmartMerge] = useState(false);
  const [smartMergeData, setSmartMergeData] = useState(null);
  const [analyzingColumns, setAnalyzingColumns] = useState(false);

  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();

  // Fetch databases for dropdown
  const {
    data: databasesData,
    isLoading: databasesLoading,
    error: databasesError,
  } = useQuery({
    queryKey: ["databases-dropdown"],
    queryFn: () => databaseAPI.getDatabasesDropdown(),
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: 3,
    onError: (error) => {
      console.error("Failed to fetch databases:", error);
      enqueueSnackbar("Failed to load databases", { variant: "error" });
    },
  });

  const databases = databasesData?.data?.databases || [];

  // Tables for selected database
  const [tables, setTables] = useState([]);
  const [tablesLoading, setTablesLoading] = useState(false);

  // Initialize database_id and set default database if available
  React.useEffect(() => {
    if (!databasesLoading && databasesData && databases.length > 0) {
      // Auto-select the first database (default google_db) for convenience
      if (!formData.database_id || formData.database_id === undefined) {
        setFormData((prev) => ({
          ...prev,
          database_id: databases[0]?.id || "",
        }));
      }
    }
  }, [databasesLoading, databasesData, databases, formData.database_id]);

  // Load tables whenever a database is selected
  React.useEffect(() => {
    const loadTables = async () => {
      if (!formData.database_id) {
        setTables([]);
        return;
      }
      const selectedDb = databases.find((db) => db.id === formData.database_id);
      if (!selectedDb) return;
      try {
        setTablesLoading(true);
        const res = await databaseAPI.listTables(selectedDb.database_name);
        const names = (res.data?.tables || []).map((t) => t.table_name);
        setTables(names);
      } catch (e) {
        setTables([]);
      } finally {
        setTablesLoading(false);
      }
    };
    loadTables();
  }, [formData.database_id, databases]);

  const onDrop = useCallback(
    async (acceptedFiles) => {
      if (acceptedFiles.length > 0) {
        const file = acceptedFiles[0];
        setSelectedFile(file);

        // Auto-generate table name from filename
        try {
          const response = await uploadsAPI.previewTableName(file.name);
          const suggestedName = response.data.suggested_table_name;
          setSuggestedTableName(suggestedName);

          // Set the table name if not already set by user
          if (!formData.target_table) {
            setFormData((prev) => ({ ...prev, target_table: suggestedName }));
            setTableNameAutoGenerated(true);
          }
        } catch (error) {
          console.warn("Failed to generate table name:", error);
          setSuggestedTableName("");
        }

        // Clear any existing duplicate warning
        setDuplicateWarning(null);

        // Check for duplicates if database is already selected
        if (formData.database_id) {
          checkForDuplicate(file.name, formData.database_id);
        }
      }
    },
    [formData.target_table, formData.database_id]
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [
        ".xlsx",
      ],
      "application/vnd.ms-excel": [".xls"],
      "text/csv": [".csv"],
    },
    multiple: false,
    maxSize: 1024 * 1024 * 1024, // 1GB
  });

  // Function to check for duplicate files
  const checkForDuplicate = async (filename, databaseId) => {
    if (!filename || !databaseId) return false;

    setCheckingDuplicate(true);
    try {
      const response = await uploadsAPI.checkDuplicate(filename, databaseId);
      const isDuplicate = !!response.data.is_duplicate;
      if (isDuplicate) {
        setDuplicateWarning(response.data);
      } else {
        setDuplicateWarning(null);
      }
      return isDuplicate;
    } catch (error) {
      console.warn("Failed to check for duplicates:", error);
      setDuplicateWarning(null);
      return false;
    } finally {
      setCheckingDuplicate(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));

    // When database changes, check for duplicates
    if (field === "database_id" && selectedFile) {
      checkForDuplicate(selectedFile.name, value);
    }

    // When table name is manually changed, mark as not auto-generated
    if (field === "target_table") {
      setTableNameAutoGenerated(false);
    }
  };

  // Column mapping handlers
  const handleColumnMappingPreview = async () => {
    if (
      !selectedFile ||
      !formData.database_id ||
      !formData.target_table ||
      formData.create_new_table
    ) {
      return;
    }

    try {
      // Find the selected database to get its name
      const selectedDatabase = databases.find(
        (db) => db.id === formData.database_id
      );
      if (!selectedDatabase) {
        enqueueSnackbar("Selected database not found", { variant: "error" });
        return;
      }

      const previewData = new FormData();
      previewData.append("file", selectedFile);
      previewData.append("database_name", selectedDatabase.database_name); // Backend expects database_name
      previewData.append("table_name", formData.target_table);

      const response = await columnMappingAPI.previewMapping(previewData);
      setColumnMappings(response.data.mappings);
      setShowColumnMapping(true);
    } catch (error) {
      enqueueSnackbar(
        error.response?.data?.error || "Failed to preview column mapping",
        {
          variant: "error",
        }
      );
    }
  };

  const handleColumnMappingConfirm = (mappings) => {
    setColumnMappings(mappings);
    setShowColumnMapping(false);
    // Proceed with upload
    handleUpload();
  };

  const handleColumnMappingCancel = () => {
    setShowColumnMapping(false);
    setColumnMappings(null);
  };

  // Smart merge handlers
  const handleSmartMergeAnalysis = async () => {
    if (!selectedFile || !formData.target_table) {
      enqueueSnackbar("File and table name are required for smart merge", {
        variant: "error",
      });
      return;
    }

    setAnalyzingColumns(true);
    try {
      const analysisData = new FormData();
      analysisData.append("file", selectedFile);
      analysisData.append("table_name", formData.target_table);

      const { data } = await smartMergeAPI.analyzeColumns(analysisData);
      setSmartMergeData(data);
      setShowSmartMerge(true);
    } catch (error) {
      enqueueSnackbar(
        error.response?.data?.error || "Failed to analyze columns for smart merge",
        { variant: "error" }
      );
    } finally {
      setAnalyzingColumns(false);
    }
  };

  const handleSmartMergeConfirm = async (mergeConfig) => {
    setShowSmartMerge(false);
    setUploading(true);
    setUploadProgress(0);

    try {
      const mergeFormData = new FormData();
      mergeFormData.append("file", selectedFile);
      mergeFormData.append("table_name", formData.target_table);
      mergeFormData.append("merge_config", JSON.stringify(mergeConfig));

      const { data: start } = await smartMergeAPI.executeMerge(mergeFormData);

      enqueueSnackbar("Smart merge started. Tracking progress...", { variant: "info" });

      // Poll for completion
      const taskId = start.task_id;
      let attempts = 0;
      const maxAttempts = 180; // up to ~9 minutes at 3s interval
      const pollIntervalMs = 3000;

      const wait = (ms) => new Promise((res) => setTimeout(res, ms));

      while (attempts < maxAttempts) {
        attempts += 1;
        try {
          const { data: status } = await smartMergeAPI.getMergeStatus(taskId);
          if (status.state === "SUCCESS") {
            const r = status.result || {};
            enqueueSnackbar(
              `Smart merge completed. Processed: ${r.processed_rows || 0}, Duplicates skipped: ${r.duplicate_count || 0}, Errors: ${r.error_count || 0}, New columns: ${r.new_columns_added || 0}, Mapped columns: ${r.columns_mapped || 0}`,
              { variant: "success", autoHideDuration: 8000 }
            );
            break;
          }
          if (status.state === "PROGRESS") {
            setUploadProgress(Math.round(status.progress || 0));
          }
        } catch (e) {
          // ignore transient errors
        }
        await wait(pollIntervalMs);
      }

      if (attempts >= maxAttempts) {
        enqueueSnackbar("Smart merge is still running. You can continue working.", { variant: "warning" });
      }

      queryClient.invalidateQueries({ queryKey: ["uploads"] });

      // Reset form and close dialog
      setSelectedFile(null);
      setFormData({
        category_id: "",
        database_id: "",
        target_table: "",
        create_new_table: false,
      });
      setDuplicateWarning(null);
      setSmartMergeData(null);
      onClose();
      
    } catch (error) {
      enqueueSnackbar(
        error.response?.data?.error || "Failed to execute smart merge",
        { variant: "error" }
      );
    } finally {
      setUploading(false);
    }
  };

  const handleSmartMergeCancel = () => {
    setShowSmartMerge(false);
    setSmartMergeData(null);
  };

  const handleUpload = async () => {
    if (!selectedFile || !formData.database_id || !formData.target_table) {
      enqueueSnackbar("Please fill in all required fields", {
        variant: "error",
      });
      return;
    }

    // Always perform a duplicate check and continue based on result
    const isDuplicate = await checkForDuplicate(selectedFile.name, formData.database_id);

    // Handle duplicate upload with smart merge option
    if (isDuplicate || duplicateWarning?.is_duplicate) {
      const choice = window.confirm(
        `This file has already been uploaded to the selected database on ${new Date(
          duplicateWarning.duplicate_details.uploaded_date
        ).toLocaleDateString()}.\n\nChoose:\n- OK: Smart merge (compare columns and merge data)\n- Cancel: Stop upload`
      );
      
      if (!choice) return;
      
      // If user chooses smart merge, analyze columns first
      await handleSmartMergeAnalysis();
      return;
    }

    // Check if column mapping is needed for existing tables
    if (!formData.create_new_table && !columnMappings && formData.database_id) {
      const selectedDb = databases.find((db) => db.id === formData.database_id);

      // If database has no tables, automatically create new table
      if (selectedDb && selectedDb.total_tables === 0) {
        setFormData((prev) => ({ ...prev, create_new_table: true }));
      }
      // If database has tables, check if the specific target table exists
      else if (selectedDb && selectedDb.total_tables > 0) {
        try {
          // Check if the target table exists by fetching database tables
          const tablesResponse = await databaseAPI.listTables(
            selectedDb.database_name
          );
          const tableExists = tablesResponse.data.tables.some(
            (table) => table.table_name === formData.target_table
          );

          if (tableExists) {
            // Table exists, show column mapping preview
            await handleColumnMappingPreview();
            return;
          } else {
            // Table doesn't exist, create new table
            setFormData((prev) => ({ ...prev, create_new_table: true }));
          }
        } catch (error) {
          console.warn(
            "Failed to check if table exists, defaulting to create new table:",
            error
          );
          setFormData((prev) => ({ ...prev, create_new_table: true }));
        }
      }
    }

    setUploading(true);
    setUploadProgress(0);

    try {
      const uploadFormData = new FormData();
      uploadFormData.append("file", selectedFile);
      uploadFormData.append("category_id", formData.category_id);
      uploadFormData.append("database_id", formData.database_id); // Changed from target_database
      uploadFormData.append("target_table", formData.target_table);

      uploadFormData.append("create_new_table", formData.create_new_table);

      // Add new database name if creating new database

      // Add column mappings if available
      if (columnMappings) {
        uploadFormData.append(
          "column_mappings",
          JSON.stringify(columnMappings)
        );
      }

      await uploadsAPI.uploadFile(uploadFormData, (progressEvent) => {
        const progress = Math.round(
          (progressEvent.loaded * 100) / progressEvent.total
        );
        setUploadProgress(progress);
      });

      enqueueSnackbar("File uploaded successfully! Processing started.", {
        variant: "success",
      });
      queryClient.invalidateQueries({ queryKey: ["uploads"] });

      // Reset form
      setSelectedFile(null);
      setFormData({
        category_id: "",
        database_id: "", // Changed from target_database
        target_table: "",

        create_new_table: false,
      });
      setUploadProgress(0);
      setSuggestedTableName("");
      setTableNameAutoGenerated(false);
      setDuplicateWarning(null);
      setColumnMappings(null);
      setShowColumnMapping(false);
      onClose();
    } catch (error) {
      enqueueSnackbar(error.response?.data?.error || "Upload failed", {
        variant: "error",
      });
    } finally {
      setUploading(false);
    }
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitle>Upload Excel File</DialogTitle>
        <DialogContent>
          <Box sx={{ mb: 3 }}>
            <Box
              {...getRootProps()}
              sx={{
                border: "2px dashed",
                borderColor: isDragActive ? "primary.main" : "grey.300",
                borderRadius: 2,
                p: 3,
                textAlign: "center",
                cursor: "pointer",
                backgroundColor: isDragActive ? "primary.light" : "grey.50",
                transition: "all 0.3s ease",
              }}
            >
              <input {...getInputProps()} />
              <UploadIcon sx={{ fontSize: 48, color: "grey.500", mb: 2 }} />
              {selectedFile ? (
                <Typography variant="body1">
                  Selected: {selectedFile.name}
                </Typography>
              ) : (
                <Typography variant="body1">
                  Drag and drop an Excel file here, or click to select
                </Typography>
              )}
              <Typography variant="caption" color="text.secondary">
                Supported formats: .xlsx, .xls, .csv (Max: 1GB)
              </Typography>
            </Box>
          </Box>

          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField
                select
                fullWidth
                label="Category (Optional)"
                value={formData.category_id}
                onChange={(e) =>
                  handleInputChange("category_id", e.target.value)
                }
              >
                <MenuItem value="">None</MenuItem>
                {categories?.map((category) => (
                  <MenuItem key={category.id} value={category.id}>
                    {category.full_path}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                select
                fullWidth
                required
                label="Target Database"
                value={formData.database_id || ""}
                onChange={(e) =>
                  handleInputChange("database_id", e.target.value)
                }
                helperText="Select database where data will be stored"
              >
                <MenuItem value="">Select a database</MenuItem>
                {/* For google_db, we need to find its ID from the databases array or handle it specially */}
                {databases &&
                  databases.map((database) => (
                    <MenuItem key={database.id} value={database.id}>
                      {database.database_name === "google_db"
                        ? `Default Database (${database.database_name})`
                        : `${database.database_name} (${
                            database.total_tables || 0
                          } tables)`}
                    </MenuItem>
                  ))}
              </TextField>
            </Grid>

            <Grid item xs={12} md={6}>
              <Autocomplete
                freeSolo
                openOnFocus
                options={tables}
                loading={tablesLoading}
                disabled={!formData.database_id}
                value={formData.target_table || ""}
                onInputChange={(event, newValue) => {
                  handleInputChange("target_table", newValue);
                  // mark create_new_table depending on existence
                  const exists = tables.includes((newValue || "").trim());
                  setFormData((prev) => ({ ...prev, create_new_table: !exists }));
                }}
                onChange={(event, newValue) => {
                  // When user picks from dropdown
                  if (typeof newValue === 'string') {
                    handleInputChange("target_table", newValue);
                    const exists = tables.includes((newValue || "").trim());
                    setFormData((prev) => ({ ...prev, create_new_table: !exists }));
                  }
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Target Table"
                    helperText={
                      tableNameAutoGenerated
                        ? `Auto-generated from filename${
                            suggestedTableName ? ` (${suggestedTableName})` : ""
                          }`
                        : !formData.database_id
                          ? "Select a database first"
                          : (tables.length > 0
                              ? "Choose an existing table or type a new table name"
                              : "No tables yet. Type a new table name to create one")
                    }
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {tableNameAutoGenerated && (
                            <AutoIcon color="action" sx={{ fontSize: 20, mr: 1 }} />
                          )}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />
            </Grid>
          </Grid>

          {/* Duplicate Error */}
          {duplicateWarning?.is_duplicate && (
            <Alert 
              severity="error" 
              sx={{ 
                mt: 2, 
                backgroundColor: '#fff3e0',
                border: '1px solid #ff9800',
                '& .MuiAlert-icon': {
                  color: '#ff9800'
                }
              }} 
              icon={<WarningIcon />}
            >
              <Typography variant="body2" fontWeight="bold" sx={{ color: '#e65100' }}>
                Duplicate File Detected
              </Typography>
              <Typography variant="body2" sx={{ color: '#bf360c' }}>
                This file was already uploaded on{" "}
                {new Date(
                  duplicateWarning.duplicate_details.uploaded_date
                ).toLocaleDateString()}{" "}
                to table "{duplicateWarning.duplicate_details.table_name}". Status: {duplicateWarning.duplicate_details.status}
              </Typography>
            </Alert>
          )}

          {/* Checking duplicate indicator */}
          {checkingDuplicate && (
            <Box sx={{ mt: 2, display: "flex", alignItems: "center", gap: 1 }}>
              <CircularProgress size={16} />
              <Typography variant="body2" color="text.secondary">
                Checking for duplicates...
              </Typography>
            </Box>
          )}

          {/* Analyzing columns indicator */}
          {analyzingColumns && (
            <Box sx={{ mt: 2, display: "flex", alignItems: "center", gap: 1 }}>
              <CircularProgress size={16} />
              <Typography variant="body2" color="text.secondary">
                Analyzing columns for smart merge...
              </Typography>
            </Box>
          )}

          {uploading && (
            <Box sx={{ mt: 3 }}>
              <Typography variant="body2" gutterBottom>
                Upload Progress: {uploadProgress}%
              </Typography>
              <LinearProgress variant="determinate" value={uploadProgress} />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={uploading}>
            Cancel
          </Button>
          <Button
            onClick={handleUpload}
            variant="contained"
            disabled={!selectedFile || uploading}
            startIcon={
              uploading ? <CircularProgress size={20} /> : <UploadIcon />
            }
            color={duplicateWarning?.is_duplicate ? "warning" : "primary"}
          >
            {uploading ? "Uploading..." : duplicateWarning?.is_duplicate ? "Smart Merge" : "Upload"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Column Mapping Dialog */}
      <ColumnMappingDialog
        open={showColumnMapping}
        mappings={columnMappings}
        onConfirm={handleColumnMappingConfirm}
        onCancel={handleColumnMappingCancel}
      />

      {/* Smart Merge Dialog */}
      <ColumnMergeDialog
        open={showSmartMerge}
        existingColumns={smartMergeData?.existing_table?.columns || []}
        newColumns={smartMergeData?.new_file?.columns || []}
        tableName={smartMergeData?.existing_table?.name || formData.target_table}
        onConfirmMerge={handleSmartMergeConfirm}
        onClose={handleSmartMergeCancel}
        loading={analyzingColumns}
      />
    </>
  );
};

const Uploads = () => {
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [statusFilter, setStatusFilter] = useState("");
  const [deleteDialog, setDeleteDialog] = useState({
    open: false,
    type: null,
    item: null,
  });

  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  // Fetch uploads
  const {
    data: uploadsData,
    isLoading: uploadsLoading,
    error: uploadsError,
  } = useQuery({
    queryKey: ["uploads", page, perPage, statusFilter],
    queryFn: () => uploadsAPI.getUploads(page, perPage, statusFilter || null),
    keepPreviousData: true,
    refetchInterval: 5000, // Refresh every 5 seconds for status updates
  });

  // Fetch categories for upload dialog
  const { data: categoriesData } = useQuery({
    queryKey: ["categories-flat"],
    queryFn: () => categoriesAPI.getCategories({ include_children: false }),
    staleTime: 10 * 60 * 1000, // 10 minutes
  });

  // Cancel upload mutation
  const cancelUploadMutation = useMutation({
    mutationFn: uploadsAPI.cancelUpload,
    onSuccess: () => {
      enqueueSnackbar("Upload cancelled successfully", { variant: "success" });
      queryClient.invalidateQueries({ queryKey: ["uploads"] });
    },
    onError: (error) => {
      enqueueSnackbar(
        error.response?.data?.error || "Failed to cancel upload",
        { variant: "error" }
      );
    },
  });

  // Retry upload mutation
  const retryUploadMutation = useMutation({
    mutationFn: uploadsAPI.retryUpload,
    onSuccess: () => {
      enqueueSnackbar("Upload retry started", { variant: "success" });
      queryClient.invalidateQueries({ queryKey: ["uploads"] });
    },
    onError: (error) => {
      enqueueSnackbar(error.response?.data?.error || "Failed to retry upload", {
        variant: "error",
      });
    },
  });

  // Commit staged data mutation
  const commitUploadMutation = useMutation({
    mutationFn: (uploadId) => uploadsAPI.commitUpload(uploadId),
    onSuccess: () => {
      enqueueSnackbar("Data committed to original table", { variant: "success" });
      queryClient.invalidateQueries({ queryKey: ["uploads"] });
    },
    onError: (error) => {
      enqueueSnackbar(error.response?.data?.error || "Failed to commit data", {
        variant: "error",
      });
    },
  });

  // Delete upload mutation
  const deleteUploadMutation = useMutation({
    mutationFn: (uploadId) => {
      // Calling deleteUpload API
      return uploadsAPI.deleteUpload(uploadId);
    },
    onSuccess: (data) => {
      // Delete upload successful
      // Use sync service for comprehensive refresh
      SyncService.syncAfterUploadDeletion();
      enqueueSnackbar("Upload deleted successfully", { variant: "success" });
    },
    onError: (error) => {
      console.error("Delete upload error:", error);
      console.error("Error response:", error.response);
      enqueueSnackbar(
        error.response?.data?.error || "Failed to delete upload",
        { variant: "error" }
      );
    },
  });

  const uploads = uploadsData?.data?.uploads || [];
  const pagination = uploadsData?.data?.pagination || {};
  const categories = categoriesData?.data?.categories || [];

  // Handler functions
  const handleDeleteUpload = (upload) => {
    // Delete upload requested
    setDeleteDialog({ open: true, type: "upload", item: upload });
  };

  const handleConfirmDelete = () => {
    // Delete upload confirmed
    deleteUploadMutation.mutate(deleteDialog.item.id);
    setDeleteDialog({ open: false, type: null, item: null });
  };

  const formatFilesize = (bytes) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };

  if (uploadsLoading && uploads.length === 0) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="50vh"
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}
      >
        <Typography variant="h4" fontWeight="bold">
          File Uploads
        </Typography>
        <Box sx={{ display: "flex", gap: 2 }}>
          <Button
            variant="contained"
            startIcon={<UploadIcon />}
            onClick={() => setUploadDialogOpen(true)}
          >
            Upload File
          </Button>
        </Box>
      </Box>

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={4}>
              <TextField
                select
                fullWidth
                label="Filter by Status"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <MenuItem value="">All Statuses</MenuItem>
                <MenuItem value="pending">Pending</MenuItem>
                <MenuItem value="processing">Processing</MenuItem>
                <MenuItem value="completed">Completed</MenuItem>
                <MenuItem value="failed">Failed</MenuItem>
                <MenuItem value="cancelled">Cancelled</MenuItem>
              </TextField>
            </Grid>
            <Grid item>
              <Button
                startIcon={<RefreshIcon />}
                onClick={() =>
                  queryClient.invalidateQueries({ queryKey: ["uploads"] })
                }
              >
                Refresh
              </Button>
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                select
                fullWidth
                label="Rows per page"
                value={perPage}
                onChange={(e) => {
                  setPerPage(Number(e.target.value));
                  setPage(1);
                }}
              >
                <MenuItem value={10}>10</MenuItem>
                <MenuItem value={25}>25</MenuItem>
                <MenuItem value={50}>50</MenuItem>
              </TextField>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Error Alert */}
      {uploadsError && (
        <Alert severity="error" sx={{ mb: 3 }}>
          Failed to load uploads:{" "}
          {uploadsError.response?.data?.error || uploadsError.message}
        </Alert>
      )}

      {/* Uploads Table */}
      <Card>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>File Name</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Progress</TableCell>
                <TableCell>Database/Table</TableCell>
                <TableCell>File Size</TableCell>
                <TableCell>Created</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {uploads.map((upload) => (
                <TableRow key={upload.id}>
                  <TableCell>
                    <Typography variant="body2" fontWeight="bold">
                      {upload.original_filename}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <StatusChip status={upload.status} />
                  </TableCell>
                  <TableCell>
                    <Box sx={{ width: 100 }}>
                      <LinearProgress
                        variant="determinate"
                        value={upload.progress_percentage || 0}
                      />
                      <Typography variant="caption">
                        {Math.round(upload.progress_percentage || 0)}%
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {upload.target_database}.{upload.target_table}
                    </Typography>
                  </TableCell>
                  <TableCell>{formatFilesize(upload.file_size)}</TableCell>
                  <TableCell>
                    {format(new Date(upload.created_at), "MMM dd, yyyy HH:mm")}
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: "flex", gap: 1 }}>
                      {upload.status === "processing" && (
                        <IconButton
                          size="small"
                          onClick={() => cancelUploadMutation.mutate(upload.id)}
                          disabled={cancelUploadMutation.isLoading}
                          title="Cancel upload"
                        >
                          <CancelIcon />
                        </IconButton>
                      )}
                      {upload.status === "failed" && (
                        <IconButton
                          size="small"
                          onClick={() => retryUploadMutation.mutate(upload.id)}
                          disabled={retryUploadMutation.isLoading}
                          title="Retry upload"
                        >
                          <RetryIcon />
                        </IconButton>
                      )}
                      {/* View details button removed as requested */}
                      {upload.status === "completed" && upload.column_mappings?.staging && !upload.column_mappings?.staging?.transferred && (
                        <Tooltip title="Commit data from temporary table to original table">
                          <span>
                            <Button
                              size="small"
                              variant="outlined"
                              onClick={() => commitUploadMutation.mutate(upload.id)}
                              disabled={commitUploadMutation.isLoading}
                            >
                              Commit
                            </Button>
                          </span>
                        </Tooltip>
                      )}
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleDeleteUpload(upload)}
                        disabled={deleteUploadMutation.isLoading}
                        title="Delete upload"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        {uploads.length === 0 && (
          <Box sx={{ p: 4, textAlign: "center" }}>
            <Typography color="text.secondary">No uploads found</Typography>
          </Box>
        )}
      </Card>

      {/* Pagination */}
      {pagination?.pages > 1 && (
        <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
          <Pagination
            count={pagination.pages}
            page={page}
            onChange={(e, value) => setPage(value)}
            color="primary"
          />
        </Box>
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialog.open}
        onClose={() => setDeleteDialog({ open: false, type: null, item: null })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <WarningIcon color="error" />
          Confirm Deletion
        </DialogTitle>
        <DialogContent>
          <Alert severity="error" sx={{ mb: 2 }}>
            This action cannot be undone!
          </Alert>
          {deleteDialog.type === "upload" && (
            <Box>
              <Typography gutterBottom>
                Are you sure you want to delete{" "}
                <strong>"{deleteDialog.item?.original_filename}"</strong>?
              </Typography>
              <Typography variant="body2" sx={{ mt: 2, mb: 1 }}>
                This will:
              </Typography>
              <Box component="ul" sx={{ pl: 2, m: 0 }}>
                <Typography component="li" variant="body2">
                  Delete the upload record and its associated file
                </Typography>
                <Typography component="li" variant="body2">
                  Cancel any running upload tasks for this file
                </Typography>
                <Typography component="li" variant="body2">
                  Remove all data associated with this upload
                </Typography>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() =>
              setDeleteDialog({ open: false, type: null, item: null })
            }
            disabled={deleteUploadMutation.isLoading}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirmDelete}
            variant="contained"
            color="error"
            disabled={deleteUploadMutation.isLoading}
            startIcon={
              deleteUploadMutation.isLoading ? (
                <CircularProgress size={16} />
              ) : (
                <DeleteIcon />
              )
            }
          >
            {deleteUploadMutation.isLoading ? "Deleting..." : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Upload Dialog */}
      <UploadDialog
        open={uploadDialogOpen}
        onClose={() => setUploadDialogOpen(false)}
        categories={categories}
      />
    </Box>
  );
};

export default Uploads;
